<template>
  <v-container fluid class="pl-4 pr-4 pb-4 pt-1">
    <!-- Header with actions and filter toggle -->
    <v-row align="center" class="mb-0 px-0" style="min-height: 48px;">
      <v-col cols="auto">
        <v-btn 
          color="primary"
          @click="openCreateDialog"
          prepend-icon="mdi-plus"
        >
          Добавить
        </v-btn>
        <v-btn
          v-if="selectedItems.length > 0"
          color="error"
          variant="outlined"
          class="ml-2"
          @click="bulkDelete"
          prepend-icon="mdi-delete"
        >
          Удалить ({{ selectedItems.length }})
        </v-btn>
      </v-col>
      <v-col class="text-center">
        <h4 class="mb-0">География</h4>
      </v-col>
      <v-col cols="auto">
        <!-- Pagination controls with total count -->
        <div class="d-flex align-center ga-2">
          <span class="text-caption">На странице:</span>
          <v-select
            v-model="limit"
            :items="[5, 10, 20, 50, 100, 250, 500]"
            variant="outlined"
            density="compact"
            hide-details
            style="max-width: 160px"
            @update:model-value="onPageChange(1)"
          ></v-select>
          <v-pagination
            v-model="currentPage"
            :length="totalPages"
            :total-visible="5"
            size="small"
            @update:model-value="onPageChange"
          ></v-pagination>
          <span class="text-caption font-weight-bold"> Найдено: {{ totalItems }}</span>
        </div>
      </v-col>
      <v-col cols="auto">
        <v-btn
          :icon="showFilters ? 'mdi-filter-off' : 'mdi-filter'"
          variant="outlined"
          @click="toggleFilters"
          :color="showFilters ? 'primary' : 'default'"
        >
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <!-- Main table column -->
      <v-col :cols="showFilters ? 10 : 12" class="table-column-transition">
        <!-- Table section -->
        <v-card>
          <v-card-text class="pa-0">
            <v-data-table
              :headers="tableHeaders"
              :items="items"
              :loading="loading"
              :items-per-page="limit"
              class="elevation-1"
              hover
              fixed-header
              height="calc(100vh - 301px)"
              show-select
              v-model="selectedItems"
              item-value="id"
              hide-default-footer
            >
              <!-- Custom table columns -->
              <template v-slot:[`item.name`]="{ item }">
                <div>
                  <div class="font-weight-medium">{{ item.name }}</div>
                  <div class="text-caption text-grey-darken-1">{{ item.path }} ({{ item.level }})</div>
                </div>
              </template>

              <!-- Actions column -->
              <template v-slot:[`item.actions`]="{ item }">
                <v-btn
                  size="small"
                  variant="text"
                  icon="mdi-pencil"
                  @click="editItem(item)"
                ></v-btn>
                <v-btn
                  size="small"
                  variant="text"
                  icon="mdi-delete"
                  color="error"
                  @click="deleteItem(item)"
                ></v-btn>
              </template>

              <!-- No data state -->
              <template v-slot:no-data>
                <div class="text-center py-6">
                  <v-icon size="64" color="grey-lighten-2">mdi-map</v-icon>
                  <p class="text-h6 text-grey-darken-1 mt-4">Локации не найдены</p>
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Filters column with transition -->
      <v-slide-x-reverse-transition>
        <v-col v-if="showFilters" cols="2" key="filters">
          <v-card>
            <v-card-title class="text-subtitle-1">Фильтры</v-card-title>
            <v-card-text class="pa-3">
            <!-- Name filter -->
            <v-text-field
              v-model="filters.name"
              label="Название локации"
              placeholder="Поиск по названию..."
              prepend-inner-icon="mdi-magnify"
              variant="outlined"
              density="compact"
              hide-details
              clearable
              class="mb-4"
              @input="debouncedSearch"
            ></v-text-field>

            <!-- Path filter -->
            <v-text-field
              v-model="filters.path"
              label="Иерархический путь"
              placeholder="Поиск по пути..."
              prepend-inner-icon="mdi-sitemap"
              variant="outlined"
              density="compact"
              hide-details
              clearable
              class="mb-4"
              @input="debouncedSearch"
            ></v-text-field>

            <!-- Level filter -->
            <v-select
              v-model="filters.level"
              :items="levelOptions"
              label="Уровень иерархии"
              prepend-inner-icon="mdi-layers"
              variant="outlined"
              density="compact"
              hide-details
              clearable
              class="mb-4"
              @update:model-value="onFilterChange"
            ></v-select>

            <!-- Parent path filter -->
            <v-autocomplete
              v-model="filters.parentPath"
              :items="parentPathOptions"
              :loading="loadingParents"
              label="Родительская локация"
              prepend-inner-icon="mdi-map-marker-path"
              variant="outlined"
              density="compact"
              hide-details
              clearable
              class="mb-4"
              no-data-text="Нет доступных родительских локаций"
              @update:model-value="onFilterChange"
            ></v-autocomplete>

            <!-- Filter actions -->
            <div class="d-flex justify-space-between">
              <v-btn
                color="primary"
                variant="outlined"
                @click="resetFilters"
                prepend-icon="mdi-filter-remove"
              >
                Сбросить
              </v-btn>
              <v-chip
                v-if="hasActiveFilters"
                color="primary"
                variant="outlined"
                size="small"
              >
                {{ activeFiltersCount }} активных
              </v-chip>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-slide-x-reverse-transition>
  </v-row>

    <!-- Create/Edit Dialog -->
    <v-dialog v-model="createDialogVisible" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="text-h5">{{ editingItem ? 'Редактировать' : 'Добавить' }} локацию</span>
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="formItem.name"
            label="Название локации"
            placeholder="Россия, Москва, Центральный округ..."
            :rules="nameRules"
            required
            density="compact"
            class="mb-3"
          ></v-text-field>
          
          <v-text-field
            v-model="formItem.path"
            label="Иерархический путь"
            placeholder="russia, russia.moscow, russia.moscow.central..."
            :rules="pathRules"
            required
            density="compact"
            class="mb-3"
          ></v-text-field>

          <v-select
            v-model="formItem.level"
            :items="geoLevelOptions"
            label="Географический уровень"
            :rules="levelRules"
            required
            density="compact"
            class="mb-3"
          ></v-select>

          <v-alert
            type="info"
            variant="tonal"
            density="compact"
            class="mb-3"
          >
            Путь определяет место локации в иерархии. Используйте точки для разделения уровней.
            Например: "russia.moscow" для города "Москва" в стране "Россия".
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="closeCreateDialog">Отменить</v-btn>
          <v-btn color="primary" @click="saveItem">{{ editingItem ? 'Сохранить' : 'Создать' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Error snackbar -->
    <v-snackbar v-model="errorSnackbar" color="error" timeout="4000">
      {{ errorMessage }}
      <template v-slot:actions>
        <v-btn color="white" variant="text" @click="errorSnackbar = false">Закрыть</v-btn>
      </template>
    </v-snackbar>

    <!-- Success snackbar -->
    <v-snackbar v-model="successSnackbar" color="success" timeout="3000">
      {{ successMessage }}
      <template v-slot:actions>
        <v-btn color="white" variant="text" @click="successSnackbar = false">Закрыть</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { referenceApi } from '@/services/api/referenceApi'
import { debounce } from 'lodash'

export default {
  name: 'GeolocationsTable',
  setup() {
    const api = referenceApi
    const loadingParents = ref(false)
    const loading = ref(false)
    const items = ref([])
    const selectedItems = ref([])
    const showFilters = ref(localStorage.getItem('geolocations-filters-panel') !== 'false')
    
    // Pagination state
    const currentPage = ref(1)
    const limit = ref(25)
    const totalItems = ref(0)
    const totalPages = ref(0)
    
    // Dialog states
    const createDialogVisible = ref(false)
    const editingItem = ref(null)
    const formItem = ref({
      name: '',
      path: '',
      level: ''
    })
    
    // Snackbars
    const errorSnackbar = ref(false)
    const successSnackbar = ref(false)
    const errorMessage = ref('')
    const successMessage = ref('')

    const tableHeaders = computed(() => [
      { 
        title: 'Название', 
        key: 'name', 
        sortable: true
      },
      {
        title: 'Действия',
        key: 'actions',
        sortable: false,
        width: 120
      }
    ])

    // Filters
    const filters = ref({
      name: '',
      path: '',
      level: null,
      parentPath: null
    })

    const parentPathOptions = ref([])

    // Filter options
    const levelOptions = [
      { title: 'Тип (Россия/Зарубежье)', value: 'type' },
      { title: 'Континент', value: 'continent' },
      { title: 'Страна', value: 'country' },
      { title: 'Федеральный округ', value: 'district' },
      { title: 'Регион', value: 'region' },
      { title: 'Город', value: 'city' }
    ]

    // Geographic level options for create/edit form
    const geoLevelOptions = [
      { title: 'Тип (Россия/Зарубежье)', value: 'type' },
      { title: 'Континент', value: 'continent' },
      { title: 'Страна', value: 'country' },
      { title: 'Федеральный округ', value: 'district' },
      { title: 'Регион', value: 'region' },
      { title: 'Город', value: 'city' }
    ]

    // Computed
    const hasActiveFilters = computed(() => {
      return !!(filters.value.name || filters.value.path || filters.value.level || filters.value.parentPath)
    })

    const activeFiltersCount = computed(() => {
      let count = 0
      if (filters.value.name) count++
      if (filters.value.path) count++
      if (filters.value.level) count++
      if (filters.value.parentPath) count++
      return count
    })

    // Validation rules
    const nameRules = [
      v => !!v || 'Название локации обязательно',
      v => (v && v.length >= 1 && v.length <= 255) || 'Название должно содержать от 1 до 255 символов'
    ]

    const pathRules = [
      v => !!v || 'Иерархический путь обязателен',
      v => /^[a-z0-9_]+(\.[a-z0-9_]+)*$/.test(v) || 'Путь должен содержать только строчные латинские буквы, цифры и символ подчеркивания, разделенные точками',
      v => v.split('.').every(part => part.length > 0) || 'Каждая часть пути должна быть непустой'
    ]

    const levelRules = [
      v => !!v || 'Географический уровень обязателен'
    ]

    // Data loading
    const loadData = async ({ page, limit: pageLimit, callback }) => {
      try {
        const skip = (page - 1) * pageLimit
        const params = {
          skip,
          limit: pageLimit,
          ...getActiveFilters()
        }
        const response = await api.getGeolocations(params)
        callback(response.data)
      } catch (error) {
        console.error('Error loading geolocations:', error)
        callback(null, 'Ошибка загрузки географии. Проверьте подключение к серверу.')
      }
    }

    const loadItems = async () => {
      loading.value = true
      try {
        const skip = (currentPage.value - 1) * limit.value
        const params = {
          skip,
          limit: limit.value,
          ...getActiveFilters()
        }
        const response = await api.getGeolocations(params)
        const data = response.data
        
        items.value = data.items || data || []
        totalItems.value = data.total || 0
        totalPages.value = Math.ceil(totalItems.value / limit.value)
      } catch (error) {
        console.error('Error loading geolocations:', error)
        errorMessage.value = 'Ошибка загрузки географии. Проверьте подключение к серверу.'
        errorSnackbar.value = true
      } finally {
        loading.value = false
      }
    }

    const getActiveFilters = () => {
      const activeFilters = {}
      if (filters.value.name) activeFilters.name = filters.value.name
      if (filters.value.path) activeFilters.path = filters.value.path
      if (filters.value.level) activeFilters.level = filters.value.level
      if (filters.value.parentPath) activeFilters.parent_path = filters.value.parentPath
      return activeFilters
    }

    // Filter methods
    const resetFilters = () => {
      filters.value = {
        name: '',
        path: '',
        level: null,
        parentPath: null
      }
      currentPage.value = 1
      loadItems()
    }

    const onFilterChange = () => {
      currentPage.value = 1
      loadItems()
    }

    const debouncedSearch = debounce(() => {
      currentPage.value = 1
      loadItems()
    }, 500)
    
    // Pagination methods
    const onPageChange = (page) => {
      if (typeof page === 'number') {
        if (page < 1 || page > totalPages.value) return
        currentPage.value = page
      }
      loadItems()
    }
    
    // UI methods
    const toggleFilters = () => {
      showFilters.value = !showFilters.value
      localStorage.setItem('geolocations-filters-panel', showFilters.value)
    }
    
    // Dialog methods
    const openCreateDialog = () => {
      editingItem.value = null
      formItem.value = {
        name: '',
        path: '',
        level: ''
      }
      createDialogVisible.value = true
    }
    
    const editItem = (item) => {
      editingItem.value = item
      formItem.value = {
        name: item.name,
        path: item.path,
        level: item.level
      }
      createDialogVisible.value = true
    }
    
    const closeCreateDialog = () => {
      createDialogVisible.value = false
      editingItem.value = null
      formItem.value = {
        name: '',
        path: '',
        level: ''
      }
    }
    
    const saveItem = async () => {
      try {
        if (editingItem.value) {
          // Update existing item
          await api.updateGeolocation(editingItem.value.id, formItem.value)
          successMessage.value = 'Локация успешно обновлена'
        } else {
          // Create new item
          await api.createGeolocation(formItem.value)
          successMessage.value = 'Локация успешно создана'
        }
        successSnackbar.value = true
        closeCreateDialog()
        loadItems()
      } catch (error) {
        console.error('Error saving geolocation:', error)
        errorMessage.value = error.response?.data?.detail || error.response?.data?.message || 'Не удалось сохранить локацию'
        errorSnackbar.value = true
      }
    }
    
    const deleteItem = async (item) => {
      if (confirm(`Удалить локацию "${item.name}"?`)) {
        try {
          await api.deleteGeolocation(item.id)
          successMessage.value = 'Локация успешно удалена'
          successSnackbar.value = true
          loadItems()
        } catch (error) {
          console.error('Error deleting geolocation:', error)
          errorMessage.value = error.response?.data?.detail || error.response?.data?.message || 'Не удалось удалить локацию'
          errorSnackbar.value = true
        }
      }
    }
    
    const bulkDelete = async () => {
      if (selectedItems.value.length === 0) return
      
      if (confirm(`Удалить ${selectedItems.value.length} локаций?`)) {
        try {
          await Promise.all(selectedItems.value.map(id => api.deleteGeolocation(id)))
          successMessage.value = `Удалено ${selectedItems.value.length} локаций`
          successSnackbar.value = true
          selectedItems.value = []
          loadItems()
        } catch (error) {
          console.error('Error bulk deleting geolocations:', error)
          errorMessage.value = 'Не удалось удалить локации'
          errorSnackbar.value = true
        }
      }
    }

    const loadParentPaths = async () => {
      loadingParents.value = true
      try {
        const response = await api.getGeolocations({ limit: 1000 })
        const items = response.data.items || response.data || []
        parentPathOptions.value = items.map(item => ({
          title: `${item.name} (${item.path})`,
          value: item.path
        }))
      } catch (error) {
        console.error('Error loading parent paths:', error)
      } finally {
        loadingParents.value = false
      }
    }

    // Lifecycle
    onMounted(() => {
      loadParentPaths()
      loadItems()
    })

    return {
      // Data
      items,
      loading,
      selectedItems,
      showFilters,
      
      // Pagination
      currentPage,
      limit,
      totalItems,
      totalPages,
      
      // Dialog
      createDialogVisible,
      editingItem,
      formItem,
      
      // Snackbars
      errorSnackbar,
      successSnackbar,
      errorMessage,
      successMessage,
      
      // Computed
      tableHeaders,
      hasActiveFilters,
      activeFiltersCount,
      
      // Filters
      filters,
      parentPathOptions,
      loadingParents,
      levelOptions,
      geoLevelOptions,
      
      // Validation
      nameRules,
      pathRules,
      levelRules,
      
      // Methods
      toggleFilters,
      openCreateDialog,
      editItem,
      closeCreateDialog,
      saveItem,
      deleteItem,
      bulkDelete,
      resetFilters,
      onFilterChange,
      debouncedSearch,
      loadItems,
      loadData,
      onPageChange
    }
  }
}
</script>

<style scoped>
.table-column-transition {
  transition: flex-basis 0.3s ease, max-width 0.3s ease;
}
</style>